import { FormattedMessage, MessageDescriptor } from 'react-intl';
import styled from 'styled-components';

import { Button } from 'atoms/buttons/Button';
import { Marquee } from 'atoms/container/Marquee';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { BodyM, BrandM } from 'atoms/typography';
import { Img } from 'atoms/ui/Img';
import { NewSeasonLogo } from 'components/Season2026/NewSeasonLogo';
import { League } from 'components/landing/types';
import { useConnectionContext } from 'contexts/connection';
import { useEvents } from 'lib/events/useEvents';
import { glossary } from 'lib/glossary';
import { tabletAndAbove } from 'style/mediaQuery';

import background from './assets/background.svg';

const Wrapper = styled(Vertical).attrs({
  gap: 4,
})`
  margin-bottom: calc(var(--unit) * 12);
  padding-top: calc(var(--unit) * 6);
  background: radial-gradient(
      30% 30% at 50% 50%,
      rgba(109, 88, 33, 0.8) 0%,
      rgba(109, 88, 33, 0.4) 60%,
      transparent 100%
    ),
    linear-gradient(to bottom, transparent 0%, #000 100%), url(${background});
  background-blend-mode: lighten, lighten, normal;
  img {
    width: 100%;
  }

  @media ${tabletAndAbove} {
    gap: var(--triple-unit);
  }
`;

const TextContainer = styled(Vertical)`
  justify-content: center;
  text-align: center;
`;

const TitleText = styled.h2`
  font-size: var(--fs-landing-title);
  line-height: 1;
  font-family: var(--brand-font);
  text-transform: uppercase;
`;

const MarqueeInnerContainer = styled(Horizontal).attrs({
  center: true,
})`
  background-color: #0026ff;
  color: #00158f;
  padding: var(--intermediate-unit);
  padding-right: 0;
`;

const MarqueeContainer = styled.div`
  transform: rotate(-1.5deg);
  width: var(--100vw);
`;

const RewardsContainer = styled.div`
  display: grid;
  height: 200px;
  grid-auto-columns: 1fr;
  grid-template-columns: repeat(8, 109px);
  grid-template-rows: repeat(2, 100px);
  gap: 0;
  @media ${tabletAndAbove} {
    height: 400px;
    grid-template-columns: repeat(8, 218px);
    grid-template-rows: repeat(2, 200px);
  }
`;

const Reward = styled(Img)`
  align-self: center;
  justify-self: center;
  background-image: var(--reward);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  &:hover {
    transition: transform 0.3s ease-in-out;
    transform: scale(1.05);
  }
`;

export type RewardMarqueeAsset = {
  name: string;
  src: string;
  width: number;
  height: number;
  gridArea: string;
};

type Props = {
  league: League;
  assets: RewardMarqueeAsset[];
  messages: {
    win: MessageDescriptor;
    winDescription: MessageDescriptor;
    marquee: MessageDescriptor;
  };
};

export const Win = ({ league, assets, messages }: Props) => {
  const { signUp } = useConnectionContext();
  const track = useEvents();

  const onClick = () => {
    track('Click Landing Page Win', { clickSource: league });
    signUp();
  };

  return (
    <Wrapper>
      <Marquee>
        <RewardsContainer>
          {assets.map(asset => (
            <Reward
              key={asset.name}
              src={asset.src}
              width={asset.width}
              height={asset.height}
              style={{ gridArea: asset.gridArea }}
            />
          ))}
        </RewardsContainer>
      </Marquee>
      <TextContainer center gap={4}>
        <TitleText>
          <FormattedMessage {...messages.win} />
        </TitleText>
        <BodyM text-center>
          <FormattedMessage {...messages.winDescription} />
        </BodyM>
        <Button color="secondary" size="large" onClick={onClick}>
          <FormattedMessage {...glossary.playNow} />
        </Button>
      </TextContainer>
      <MarqueeContainer>
        <Marquee gap={0} stretch>
          <MarqueeInnerContainer gap={1}>
            <BrandM>
              <FormattedMessage {...messages.marquee} />
            </BrandM>
            <NewSeasonLogo
              size="small"
              iconSize={4}
              gap="small"
              color="#00158F"
            />
          </MarqueeInnerContainer>
        </Marquee>
      </MarqueeContainer>
    </Wrapper>
  );
};
